/**
 * #491 (PR-2) — secção «Continue de onde parou»: importações retomáveis do
 * restaurante (GET /admin/menu-imports do PR-1) montadas na fase 1 do import,
 * EXTRAÍDA por responsabilidade (R3) — o ImportMenu.tsx só ganha a montagem.
 *
 * Estados (crit. 7): a carregar, erro e lista vazia NÃO montam a secção —
 * sem spinner, sem toast; a página fica como era antes (fallback = comportamento
 * atual). Com `?id=` no URL nem monta (o ImportMenu só a monta na fase 1), logo
 * a query nem corre. Terminais (committed/cancelled/error) nunca vêm da lista.
 *
 * A11y (crit. 11): cada item é UM `<button type="button">` a toda a largura,
 * com `aria-label` = «Continuar importação de {date}» e `aria-describedby`
 * apontando à etiqueta de estado e à meta; foco visível; alvo ≥ 44px
 * (min-h-11). RTL: propriedades lógicas (ms-*, text-start) e seta
 * `rtl:rotate-180`; {date}/{pages} em <bdi>.
 */
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useI18n } from '../../lib/i18n.js'
import { Badge } from '../../components/ui/index.js'
import { importsApi } from '../../lib/api.js'
import type { ResumableImport } from '../../lib/api.js'

type Translate = (key: string, vars?: Record<string, string>) => string

/** crit. 3–4: `awaiting` vence («Aguardando sua resposta»); `extracted` =
 * «Pronta para revisar»; os outros retomáveis = «Em andamento». */
function statusBadge(item: ResumableImport, t: Translate) {
  if (item.awaiting !== 'none')
    return { label: t('admin.import.awaiting_title'), color: 'yellow' as const }
  if (item.status === 'extracted')
    return { label: t('admin.import.resume_status_ready'), color: 'indigo' as const }
  return { label: t('admin.import.resume_status_working'), color: 'gray' as const }
}

function ResumeItem({
  item,
  formatDate,
  onResume,
  t,
}: {
  item: ResumableImport
  formatDate: (at: number) => string
  onResume: (id: string) => void
  t: Translate
}) {
  const date = formatDate(item.updated_at)
  const badge = statusBadge(item, t)
  const statusId = `import-resume-status-${item.id}`
  const metaId = `import-resume-meta-${item.id}`
  return (
    <li>
      <button
        type="button"
        data-testid={`import-resume-item-${item.id}`}
        aria-label={t('admin.import.resume_cta_aria', { date })}
        aria-describedby={`${statusId} ${metaId}`}
        onClick={() => onResume(item.id)}
        className="w-full min-h-11 rounded-xl border border-orange-300 bg-orange-50 p-3 text-start space-y-1 transition-colors hover:bg-orange-100 focus-visible:ring-2 ring-orange-500 focus:outline-none"
      >
        <span className="flex flex-wrap items-center justify-between gap-2">
          <span id={statusId} className="flex items-center gap-2">
            <Badge color={badge.color}>{badge.label}</Badge>
            <bdi className="text-sm text-gray-700">{date}</bdi>
          </span>
          {/* ação visível «Continuar importação» + seta (invertida em RTL) */}
          <span className="text-sm font-bold text-orange-700">
            {t('admin.import.resume_cta')}
            <span aria-hidden="true" className="ms-1 inline-block rtl:rotate-180">
              →
            </span>
          </span>
        </span>
        <span id={metaId} className="block text-xs text-gray-600">
          {t('admin.import.resume_meta', {
            source:
              item.source === 'link'
                ? t('admin.import.resume_source_link')
                : t('admin.import.resume_source_photos'),
            pages: String(item.page_count),
          })}
        </span>
      </button>
    </li>
  )
}

export function ResumeImports({ onResume }: { onResume: (id: string) => void }) {
  const { t, lang } = useI18n()
  const [searchParams] = useSearchParams()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['menu-import-resumable'],
    queryFn: () => importsApi.listResumable(),
    // com ?id= no URL a query nem corre — a importação aberta É a página
    enabled: !searchParams.get('id'),
  })
  const items = data?.items ?? []
  if (isLoading || isError || items.length === 0) return null
  const formatDate = (at: number) =>
    // updated_at é seg Unix (coluna do servidor) — Intl formata no idioma do painel
    new Intl.DateTimeFormat(lang, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(at * 1000))
  return (
    <section
      aria-labelledby="import-resume-title"
      data-testid="import-resume"
      className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3"
    >
      <h2 id="import-resume-title" className="text-sm font-bold text-gray-900">
        {t('admin.import.resume_title')}
      </h2>
      <ul className="space-y-2">
        {items.map((item) => (
          <ResumeItem key={item.id} item={item} formatDate={formatDate} onResume={onResume} t={t} />
        ))}
      </ul>
      {/* separador + dica antes do cartão do link (UX §1): começar de novo
          volta a ler o Menu e não leva as correções já feitas */}
      <div className="space-y-1 border-t border-gray-100 pt-3">
        <p className="text-sm font-medium text-gray-700">{t('admin.import.start_new')}</p>
        <p className="text-xs text-gray-500">{t('admin.import.start_new_hint')}</p>
      </div>
    </section>
  )
}
